/**
 * LIVEMOA · BARCODE/WMS A4-1E · 내부 바코드 라벨 출력 모달.
 *
 * 원칙 (Spec §16-20):
 *   - GS1/EAN/UPC 발급 아님.  DB 에 저장된 barcode 문자열을 그대로 렌더한다.
 *   - `LM-B-*` 등 영문/숫자는 Code 128 (문자열 그대로 표현 가능).
 *   - 브라우저 print 기반 · 라벨프린터 SDK 미연동.
 *   - REUSES `jsbarcode` (이미 package.json 에 포함) · 신규 라이브러리 없음.
 *
 * Input: rows = [{ product_id, variant_key, product_name, sku, barcode, option_snapshot?, quantity? }]
 * `barcode` 미보유 row 는 렌더 목록에서 자동 스킵 + 경고.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import JsBarcode from 'jsbarcode';
import { X, Printer, AlertTriangle } from 'lucide-react';

export default function BarcodeLabelPrint({ rows, onClose }) {
  const printRef = useRef(null);
  // per-row quantity state (default 1).  Key: "pid::vk"
  const initialQty = useMemo(() => {
    const m = {};
    (rows || []).forEach((r) => {
      m[`${r.product_id}::${r.variant_key || 'default'}`] = r.quantity || 1;
    });
    return m;
  }, [rows]);
  const [qty, setQty] = useState(initialQty);

  // Filter out rows without barcode
  const printable = useMemo(
    () => (rows || []).filter((r) => (r.barcode || '').trim()),
    [rows]);
  const missing = useMemo(
    () => (rows || []).filter((r) => !(r.barcode || '').trim()),
    [rows]);

  const setQuick = (n) => {
    const m = {};
    printable.forEach((r) => {
      m[`${r.product_id}::${r.variant_key || 'default'}`] = n;
    });
    setQty(m);
  };

  // Expand to a flat list of labels (with duplicates per quantity)
  const labels = useMemo(() => {
    const out = [];
    printable.forEach((r) => {
      const k = `${r.product_id}::${r.variant_key || 'default'}`;
      const n = Math.max(1, Math.min(200, Number(qty[k]) || 1));
      for (let i = 0; i < n; i += 1) {
        out.push({ ...r, _labelIndex: i });
      }
    });
    return out;
  }, [printable, qty]);

  // Render barcodes into SVG elements after paint
  useEffect(() => {
    labels.forEach((lb, idx) => {
      const el = document.getElementById(`bc-svg-${idx}`);
      if (el && lb.barcode) {
        try {
          JsBarcode(el, String(lb.barcode), {
            format: 'CODE128',
            displayValue: false,
            height: 44,
            margin: 0,
            width: 1.6,
          });
        } catch (e) { /* silent */ }
      }
    });
  }, [labels]);

  const doPrint = () => {
    // isolate the print area · other page nodes hidden by print CSS below
    window.print();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
         data-testid="barcode-print-modal" onClick={onClose}>
      <div className="w-full sm:max-w-2xl bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white dark:bg-[#141414] border-b border-black/10 dark:border-white/10 p-4 flex justify-between items-center print:hidden">
          <div>
            <p className="text-lg font-black">바코드 라벨 출력</p>
            <p className="text-[11px] text-gray-500 mt-0.5">
              총 {labels.length}장 · Code 128 · 저장된 barcode 문자열 그대로 렌더
            </p>
          </div>
          <button onClick={onClose} className="p-1" data-testid="barcode-print-close">
            <X size={16}/>
          </button>
        </div>

        <div className="p-4 space-y-3 print:hidden">
          {/* Quick quantity */}
          <div className="flex items-center gap-2" data-testid="barcode-print-quick">
            <span className="text-xs font-black">각 항목 수량:</span>
            {[1, 5, 10].map((n) => (
              <button key={n} onClick={() => setQuick(n)}
                      className="px-3 py-1 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold hover:bg-black/5"
                      data-testid={`barcode-print-quick-${n}`}>
                {n}장
              </button>
            ))}
          </div>

          {missing.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-2 text-[11px] text-amber-800 dark:text-amber-200 flex items-start gap-1"
                 data-testid="barcode-print-missing">
              <AlertTriangle size={12} className="mt-0.5"/>
              <div>
                <b>{missing.length}건</b>은 바코드가 없어 제외되었습니다.
                상품등록에서 <b>LIVEMOA 내부 바코드 자동생성</b>을 선택하거나
                기존 EAN/UPC 를 입력해주세요.
              </div>
            </div>
          )}

          <div className="space-y-1" data-testid="barcode-print-rows">
            {printable.map((r) => {
              const k = `${r.product_id}::${r.variant_key || 'default'}`;
              return (
                <div key={k} className="flex items-center gap-2 text-xs py-1 border-b border-black/5 dark:border-white/10 last:border-0">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold truncate">{r.product_name || '(이름 없음)'}</p>
                    <p className="text-[11px] text-gray-500 truncate font-mono">
                      {(r.variant_key && r.variant_key !== 'default') ? `${r.variant_key} · ` : ''}
                      {r.barcode}
                    </p>
                  </div>
                  <input type="number" min={1} max={200}
                         value={qty[k] || 1}
                         onChange={(e) => setQty({ ...qty, [k]: e.target.value })}
                         className="w-16 px-2 py-1 text-xs rounded border border-black/10 dark:border-white/10 bg-transparent text-center"
                         data-testid={`barcode-print-qty-${r.product_id}-${r.variant_key || 'default'}`}/>
                  <span className="text-[10px] text-gray-400">장</span>
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={onClose}
                    className="flex-1 py-3 rounded-full border border-black/10 dark:border-white/10 text-xs font-black">
              취소
            </button>
            <button onClick={doPrint}
                    disabled={labels.length === 0}
                    className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-xs font-black disabled:opacity-40 inline-flex items-center justify-center gap-1"
                    data-testid="barcode-print-do">
              <Printer size={12}/> 출력 ({labels.length}장)
            </button>
          </div>
        </div>

        {/* Print area · shown only during print */}
        <div ref={printRef} className="print:block hidden print:!block" data-testid="barcode-print-area">
          <div className="p-6 grid grid-cols-3 gap-4">
            {labels.map((lb, idx) => (
              <div key={`lb-${idx}`} className="border border-black rounded-sm p-2 text-center">
                <p className="text-[8pt] font-black">LIVEMOA</p>
                <p className="text-[9pt] font-bold truncate" title={lb.product_name}>
                  {lb.product_name || '-'}
                </p>
                {lb.variant_key && lb.variant_key !== 'default' && (
                  <p className="text-[7pt] text-gray-700 truncate">{lb.variant_key}</p>
                )}
                <p className="text-[6.5pt] text-gray-600 font-mono truncate">
                  SKU: {lb.sku || '-'}
                </p>
                <svg id={`bc-svg-${idx}`} className="mx-auto"/>
                <p className="text-[7pt] font-mono truncate">{lb.barcode}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Print isolation styles */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          [data-testid="barcode-print-modal"],
          [data-testid="barcode-print-modal"] * { visibility: visible !important; }
          [data-testid="barcode-print-modal"] { position: fixed; inset: 0; background: white; }
          [data-testid="barcode-print-modal"] .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
